import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { FontSize, Text } from "../../../components/Text";
import { UpdateUserDto } from "../../../model/user/dto/UpdateUserDto";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { userActions } from "../../../redux/user/user.slice";
import { Rounded } from "../../../theme/Rounded";
import { Shadow } from "../../../theme/Shadow";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { PhoneInputComponent } from "../../../components/Inputs/PhoneInput";
import { Textarea } from "../../../components/Textarea";
import { ImageUpload } from "../../../components/ImageUpload";
import { Breakpoints } from "../../../theme/Breakpoints";

const ImageUploadWrapper = styled(Flex)`
  img {
    border-radius: ${Rounded.circle};
  }
`;

const Section = styled(Flex)`
  flex-direction: column;
  gap: 0.2rem;
`;

const SectionTitle = styled(Text)`
  font-weight: 600;
  color: ${(props) => props.theme.main};
  font-size: ${FontSize.l};
`;

const SaveButton = styled(Button)`
  height: 2.5rem;
  width: 8.5rem;
  margin: ${Spacing.s} 0 0 auto;
  padding: 0 ${Spacing.s};
`;

const InputWrapper = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
  width: 50%;
`;

const LeftSideWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  gap: ${Spacing.s};

  @media (min-width: ${Breakpoints.DESKTOP}) {
    width: 33%;
  }
`;

const Container = styled(Flex)`
  background-color: ${(props) => props.theme.white};
  border-radius: ${Rounded.md};
  box-shadow: ${Shadow.SM};
  padding: ${Spacing.m};
  flex-wrap: wrap;
`;

export const ProfileEditCompany: React.FC = () => {
  const [companyInfoUpdateLoading, setCompanyInfoUpdateLoading] =
    useState(false);

  const { t } = useTranslation();

  const loggedInUser = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const { control, setValue, handleSubmit, getValues } = useForm<
    Omit<UpdateUserDto, "userId" | "name">
  >({
    defaultValues: {
      company: loggedInUser.company || "",
      website: loggedInUser.website || "",
      phone: loggedInUser.phone || "",
      companyBio: loggedInUser.companyBio || "",
      companyPicture: loggedInUser.companyPicture || "",
    },
  });

  const dispatch = useAppDispatch();

  const updateUserCompanyInfo = async () => {
    try {
      setCompanyInfoUpdateLoading(true);

      await dispatch(
        userActions.updateUserInfo({
          ...getValues(),
          userId: loggedInUser._id!,
        }),
      ).unwrap();
      await dispatch(
        userActions.getLoggedInUser({ userId: loggedInUser._id }),
      ).unwrap();

      AtiraToast.success(t("profile.company.update.success"));
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setCompanyInfoUpdateLoading(false);
    }
  };

  return (
    <Section>
      <SectionTitle>{t("profile.title.company_info")}</SectionTitle>

      <Container>
        <LeftSideWrapper>
          <Controller
            name="companyPicture"
            control={control}
            render={({ field: { value, onChange } }) => (
              <ImageUploadWrapper>
                <ImageUpload
                  maxCount={1}
                  onRemove={() => setValue("companyPicture", "")}
                  onChange={onChange}
                  image={value || ""}
                  cropProps={{ aspectRatio: 1, aspectSlider: false }}
                />
              </ImageUploadWrapper>
            )}
          />
        </LeftSideWrapper>

        <Flex flexDirection="column" gap="l" flexGrow={1}>
          <Controller
            name="company"
            control={control}
            render={({ field: { value, onChange } }) => (
              <InputWrapper>
                <Input
                  value={value}
                  onChange={onChange}
                  title={t("common.company")}
                />
              </InputWrapper>
            )}
          />

          <Controller
            name="website"
            control={control}
            render={({ field: { value, onChange } }) => (
              <InputWrapper>
                <Input
                  value={value}
                  onChange={onChange}
                  type="text"
                  title={t("common.website")}
                />
              </InputWrapper>
            )}
          />

          <Controller
            name="companyBio"
            control={control}
            render={({ field: { value, onChange } }) => (
              <InputWrapper>
                <Textarea
                  value={value || ""}
                  onChange={onChange}
                  type="text"
                  title={t("profile.title.company_info")}
                />
              </InputWrapper>
            )}
          />

          <Controller
            name="phone"
            control={control}
            render={({ field: { value, onChange } }) => (
              <InputWrapper>
                <Text>{t("common.phone")}</Text>
                <PhoneInputComponent value={value || ""} onChange={onChange} />
              </InputWrapper>
            )}
          />

          <SaveButton
            title={t("common.save_changes").toUpperCase()}
            onClick={handleSubmit(updateUserCompanyInfo)}
            loading={companyInfoUpdateLoading}
          />
        </Flex>
      </Container>
    </Section>
  );
};
